import { useChartCtx } from '@weenat/client/dist/core/charts'
import { getMaximumFractionDigitsForChartTicks } from '@weenat/client/dist/core/charts/charts'
import { getConverters } from '@weenat/client/dist/core/units'
import { MetricWithUnit } from '@weenat/client/dist/enums/UnitChoices'
import { useConvertedValue } from '@weenat/client/dist/hooks'
import { fromColorPathToColor } from '@weenat/theme'
import { isNil } from 'lodash-es'
import { FC, useMemo } from 'react'

const TICK_LABEL_SIZE = 10

interface Tick {
  /** Displayed value (aka label) */
  value: number | string | null
  /** Pixel position on the canvas */
  yOffset: number
}

interface PureAxisProps {
  /** Text orientation - left or right */
  orientation?: 'left' | 'right'
  /** Pixel position of the axis on the canvas */
  xPos?: number
  ticks: Tick[]
  /** Space between the tick label and the actual tick */
  tickPadding?: number
  metric?: MetricWithUnit
}

const PureAxis: FC<PureAxisProps> = ({
  xPos = 0,
  orientation = 'left',
  tickPadding = 4,
  ticks,
  metric
}) => {
  return (
    <g transform={`translate(${xPos}, 0)`}>
      {ticks.map(({ value, yOffset }) => (
        <g key={`${value}_${yOffset}`} transform={`translate(0,${yOffset})`}>
          <line x2={orientation === 'left' ? -4 : +4} stroke='currentColor' />
          <text
            fontVariant={'tabular-nums'}
            textAnchor={orientation === 'left' ? 'end' : 'start'}
            fontSize={TICK_LABEL_SIZE}
            fill={!isNil(metric) ? fromColorPathToColor(`metrics.${metric}.500`) : 'black'}
            x={orientation === 'left' ? -4 - tickPadding : +4 + tickPadding}
            y={3}
          >
            {value}
          </text>
        </g>
      ))}
    </g>
  )
}

interface AxisProps {
  /** The axis position on the chart */
  position?: 'left' | 'right'
  /** Method to denormalize values (from 0 to 1 to real values) */
  denormalize?: (val: number) => number
  /** Always define. May be case when it's undefined but be aware it might break axis formatting */
  metric: MetricWithUnit | undefined
  tickPadding?: number
}

const Axis = ({ position = 'left', denormalize, metric, tickPadding }: AxisProps) => {
  const { yScale, width, marginLeft, marginRight, ticksCount } = useChartCtx()
  const { convertValue, formatConvertedValue, getUnit } = useConvertedValue()

  const axisXPosition = position === 'left' ? marginLeft : (width ?? 0) - (marginRight ?? 0)

  const unit = !isNil(metric) ? getUnit(metric) : undefined

  const ticks: Tick[] = useMemo(() => {
    if (isNil(yScale)) return []

    const tickArray = yScale.ticks(ticksCount)

    // When data is normalized we need to denormalize it to compute the number of fraction digits
    const realDomain = !isNil(denormalize) ? yScale.domain().map(denormalize) : yScale.domain()

    // When conversion is needed we convert the domain because fraction digits then depends on true values
    const convertedDomain = !isNil(metric)
      ? realDomain.map((v) => getConverters(metric)(v, unit))
      : realDomain

    const maximumFractionDigits = !isNil(metric)
      ? getMaximumFractionDigitsForChartTicks(metric, convertedDomain, unit)
      : 0

    return tickArray.map((normalizedValue) => {
      const value = denormalize ? denormalize(normalizedValue) : normalizedValue

      const formattedValue = !isNil(metric)
        ? metric === 'LW_V'
          ? formatConvertedValue({
              metric,
              value,
              displayUnit: false
            })
          : convertValue({
              metric,
              value,
              displayUnit: false,
              maximumFractionDigits
            })
        : value

      return {
        value: formattedValue,
        yOffset: yScale(normalizedValue)
      }
    })
  }, [yScale, denormalize, ticksCount, metric, unit, formatConvertedValue, convertValue])

  return (
    <PureAxis
      ticks={ticks}
      orientation={position}
      xPos={axisXPosition}
      tickPadding={tickPadding}
      metric={metric}
    />
  )
}

export default Axis
